import ProfilingInput from "./ProfilingInput";
import LoadingOverlay from "../../components/LoadingOverlay";
import ProfilingResult from "./ProfilingResult";
import { useState, useCallback, useMemo } from "react";
import { ProfilingAI } from "../../lib/profilingApi";

export default function ProfilingPage() {
  const [text, setText] = useState("");
  const [answer, setAnswer] = useState(""); // 메인 리포트(HTML)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 부가 기능(6개)
  const [featureLoading, setFeatureLoading] = useState(false);
  const [featureError, setFeatureError] = useState("");
  const [activeFeature, setActiveFeature] = useState("");
  const [featureHtml, setFeatureHtml] = useState({
    mealPlan: "",
    qa: "",
    lifestyle: "",
    exercisePlan: "",
    meditationScript: "",
    snackAlternative: "",
  });

  const lang = useMemo(() => (typeof document !== "undefined" && document.documentElement?.lang) || "ko", []);

  const onSubmit = useCallback(async () => {
    if (!text.trim()) {
      alert(lang === "zh-CN" ? "请输入患者问诊数据。" : "환자 문진 데이터를 입력해주세요.");
      return;
    }
    setLoading(true);
    setError("");
    setAnswer("");

    // 부가기능 영역 초기화
    setActiveFeature("");
    setFeatureError("");
    setFeatureHtml({
      mealPlan: "",
      qa: "",
      lifestyle: "",
      exercisePlan: "",
      meditationScript: "",
      snackAlternative: "",
    });

    try {
      const res = await ProfilingAI.report({ intakeData: text });
      setAnswer(res.html); // 섹션별 <div> HTML
    } catch (e) {
      setError(e?.message || (lang === "zh-CN" ? "生成失败" : "생성 실패"));
    } finally {
      setLoading(false);
    }
  }, [text, lang]);

  const runFeature = useCallback(
    async (type) => {
      if (!answer) return; // 프로파일링 생성 전에는 호출 못 하도록 방어
      setActiveFeature(type);
      setFeatureLoading(true);
      setFeatureError("");

      try {
        const res = await ProfilingAI.feature({ type, intakeData: text });
        setFeatureHtml((prev) => ({ ...prev, [type]: res.html }));
      } catch (e) {
        setFeatureError(e?.message || (lang === "zh-CN" ? "生成失败" : "생성 실패"));
      } finally {
        setFeatureLoading(false);
      }
    },
    [answer, text, lang]
  );

  const onCopy = useCallback(async () => {
    if (!answer) return;
    try {
      const txt = new DOMParser().parseFromString(answer, "text/html").body.textContent || "";
      await navigator.clipboard.writeText(txt);
      alert(lang === "zh-CN" ? "已复制到剪贴板。" : "클립보드에 복사되었습니다.");
    } catch {
      const el = document.createElement("textarea");
      el.value = new DOMParser().parseFromString(answer, "text/html").body.textContent || "";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      alert(lang === "zh-CN" ? "已复制到剪贴板。" : "클립보드에 복사되었습니다.");
    }
  }, [answer, lang]);

  const showFeatures = !!answer && !loading && !error;

  return (
    <section>
      <div id="content_title">
        <h2>프로파일링</h2>
        <p>
          환자의 첫 문의부터 AI가 개입하여 심층 분석 프로파일링을 생성합니다.
          <br />
          이를 통해 상담팀장은 모든 환자에게 전문가 수준의 통찰력을 가지고 첫 상담을 시작할 수 있습니다.
        </p>
      </div>

      <div className="content">
        <div>
          <ProfilingInput value={text} onChange={setText} onSubmit={onSubmit} loading={loading} />

          {/* 메인 리포트 */}
          <ProfilingResult answer={answer} error={error} loading={loading} onCopy={onCopy} />

          {/* ⬇️ 프로파일링 생성 후에만 버튼/결과 같이 노출 */}
          {showFeatures && (
            <>
              <div className="profiling-feature-toolbar">
                <button onClick={() => runFeature("mealPlan")} disabled={featureLoading}>
                  1일 맞춤 식단
                </button>
                <button onClick={() => runFeature("qa")} disabled={featureLoading}>
                  첫 상담 Q&A
                </button>
                <button onClick={() => runFeature("lifestyle")} disabled={featureLoading}>
                  생활습관 개선안
                </button>
                <button onClick={() => runFeature("exercisePlan")} disabled={featureLoading}>
                  주간 운동 계획
                </button>
                <button onClick={() => runFeature("meditationScript")} disabled={featureLoading}>
                  명상 스크립트
                </button>
                <button onClick={() => runFeature("snackAlternative")} disabled={featureLoading}>
                  건강 야식 대안
                </button>
              </div>

              <LoadingOverlay visible={featureLoading} />
              {featureError && <p className="error">{featureError}</p>}

              <div className="profiling-feature-results">
                {activeFeature === "mealPlan" && featureHtml.mealPlan && <div className="feature-block feature-mealPlan" dangerouslySetInnerHTML={{ __html: featureHtml.mealPlan }} />}
                {activeFeature === "qa" && featureHtml.qa && <div className="feature-block feature-qa" dangerouslySetInnerHTML={{ __html: featureHtml.qa }} />}
                {activeFeature === "lifestyle" && featureHtml.lifestyle && <div className="feature-block feature-lifestyle" dangerouslySetInnerHTML={{ __html: featureHtml.lifestyle }} />}
                {activeFeature === "exercisePlan" && featureHtml.exercisePlan && <div className="feature-block feature-exercisePlan" dangerouslySetInnerHTML={{ __html: featureHtml.exercisePlan }} />}
                {activeFeature === "meditationScript" && featureHtml.meditationScript && <div className="feature-block feature-meditationScript" dangerouslySetInnerHTML={{ __html: featureHtml.meditationScript }} />}
                {activeFeature === "snackAlternative" && featureHtml.snackAlternative && <div className="feature-block feature-snackAlternative" dangerouslySetInnerHTML={{ __html: featureHtml.snackAlternative }} />}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
